import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock,
  Package,
  Shield,
  Sparkles,
  TrendingUp,
  X,
} from 'lucide-react';
import { cartLineKey, useCart, type CartLine } from '../context/CartContext';
import ProductPackagingPicker from '../components/products/ProductPackagingPicker';
import { defaultSelections, formatPackagingSummary } from '../utils/packagingMath';
import { useCurrency } from '../context/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { buildLoginUrl, getReturnPathForAuthRedirect } from '../utils/authReturnUrl';
import { getRecommendationSessionId, trackRecommendationEvent } from '../utils/recommendations';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const CHECKOUT_PRICING_SNAPSHOT_KEY = 'lewouhmart_checkout_pricing_snapshot_v1';

const LB_PER_KG = 0.45359237;
const KG_TO_LB = 2.20462;

type Product = {
  id: string;
  name: string;
  imageUrl?: string | null;
  pricePerUnit: number;
  salePrice?: number | null;
  baseUnit: 'LB' | 'KG';
  currentStockLbs?: number;
  characteristics?: Record<string, unknown> | null;
  lots?: { expiryDate: string | null }[];
  isPreorderEnabled?: boolean;
  preorderEta?: string | null;
  preorderDepositValue?: number | null;
  preorderDepositType?: 'PERCENT' | 'FIXED_USD' | null;
  preorderMaxQtyLbs?: number | null;
  packagingOptions?: Array<{ id: string; label: string; sizeLbs: number }>;
};

type StorefrontPackage = {
  id: string;
  name: string;
  slug: string;
  heroImageUrl?: string | null;
  priceUsd: number;
  items?: Array<{
    qty: number;
    unit: 'LB' | 'KG' | 'PACK';
    product?: {
      id: string;
      name: string;
      pricePerUnit?: number | null;
      salePrice?: number | null;
      isActive?: boolean;
      currentStockLbs?: number | null;
      lots?: { expiryDate?: string | null }[];
    } | null;
  }>;
};

function roundToStep(value: number, decimals: number) {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

/** Parse quantity while typing stops; accepts `,` or `.` as decimal separator. */
function parseCartQuantityInput(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '' || normalized === '.' || normalized === '-') return null;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return roundToStep(n, 1);
}

function stockQtyInBaseUnit(stockLbs: number, baseUnit: 'LB' | 'KG') {
  if (baseUnit === 'KG') return roundToStep(stockLbs * LB_PER_KG, 2);
  return roundToStep(stockLbs, 1);
}

function formatQtyForInput(qty: number) {
  const rounded = roundToStep(qty, 1);
  return Number.isInteger(rounded * 10) ? rounded.toFixed(1) : String(rounded);
}

function packageItemNeedLbs(qty: number, unit: 'LB' | 'KG' | 'PACK') {
  if (unit === 'KG') return qty * KG_TO_LB;
  return qty;
}

function toLbs(qty: number, unit: 'LB' | 'KG') {
  if (unit === 'KG') return qty * KG_TO_LB;
  return qty;
}

/** Max cart quantity in the line's unit for a pre-order cap stored in lbs (null = no cap). */
function maxPreorderQtyInLineUnit(
  preorderMaxQtyLbs: number | null | undefined,
  lineUnit: 'LB' | 'KG',
): number | null {
  if (preorderMaxQtyLbs == null || preorderMaxQtyLbs <= 0) return null;
  if (lineUnit === 'LB') {
    return Math.max(0.5, floorToStep(preorderMaxQtyLbs, 0.5));
  }
  return Math.max(0.5, floorToStep(preorderMaxQtyLbs / KG_TO_LB, 0.5));
}

function floorToStep(value: number, step: number) {
  if (!Number.isFinite(value) || step <= 0) return value;
  const units = Math.floor((value + 1e-9) / step);
  return roundToStep(units * step, 2);
}

function applyFxRounding(amount: number, profile?: string): number {
  if (!Number.isFinite(amount)) return 0;
  if (profile === 'ROUND_INT') return Math.round(amount);
  if (profile === 'PSYCH_95') return Math.max(0, Math.floor(amount) + 0.95);
  if (profile === 'PSYCH_99') return Math.max(0, Math.floor(amount) + 0.99);
  return Number(amount.toFixed(2));
}

function isPackageAvailable(pkg: StorefrontPackage): boolean {
  const needByProduct = new Map<string, number>();
  const stockByProduct = new Map<string, number>();
  for (const row of pkg.items || []) {
    const pid = row.product?.id;
    if (!pid) return false;
    if (row.product?.isActive === false) return false;
    const needed = packageItemNeedLbs(Number(row.qty || 0), row.unit);
    if (!Number.isFinite(needed) || needed <= 0) return false;
    needByProduct.set(pid, (needByProduct.get(pid) ?? 0) + needed);
    stockByProduct.set(pid, Number(row.product?.currentStockLbs ?? 0));
  }
  if (!needByProduct.size) return false;
  for (const [pid, need] of needByProduct) {
    if ((stockByProduct.get(pid) ?? 0) + 1e-9 < need) return false;
  }
  return true;
}

function shelfLifeMessage(
  p: Product,
  t: (key: string, opts?: Record<string, string | number>) => string,
): string | null {
  const c = p.characteristics;
  if (c && typeof c === 'object') {
    const desc = c.shelfLifeDescription;
    if (typeof desc === 'string' && desc.trim()) return desc.trim();
    const months = Number(c.shelfLifeMonths);
    if (Number.isFinite(months) && months > 0) {
      return t('storefront.cart_shelf_life_months', { count: Math.round(months) });
    }
  }
  const earliest = p.lots?.[0]?.expiryDate;
  if (earliest) {
    const d = new Date(earliest);
    if (!Number.isNaN(d.getTime())) {
      return t('storefront.cart_best_before', { date: d.toLocaleDateString() });
    }
  }
  return null;
}

function productImageSrc(imageUrl?: string | null) {
  if (!imageUrl) return undefined;
  return imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`;
}

type CartRetentionRailProps = {
  title: string;
  subtitle?: string;
  products: Product[];
  viewAllLabel: string;
  onViewAll: () => void;
  trackContext: string;
  formatPrice: (amount: number) => string;
  addToCartLabel: string;
  onAddProduct: (product: Product) => void;
  onOpenProduct: (productId: string) => void;
};

function CartRetentionRail({
  title,
  subtitle,
  products,
  viewAllLabel,
  onViewAll,
  trackContext,
  formatPrice,
  addToCartLabel,
  onAddProduct,
  onOpenProduct,
}: CartRetentionRailProps) {
  if (products.length === 0) return null;
  return (
    <section className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">{title}</h2>
          {subtitle ? (
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600">{subtitle}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-[#00b853]/40 hover:text-[#00b853]"
        >
          {viewAllLabel}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {products.map((p) => {
          const imageSrc = productImageSrc(p.imageUrl);
          const unitPrice = Number(p.salePrice ?? p.pricePerUnit);
          const stockLbs = Number(p.currentStockLbs ?? 0);
          const canAdd = stockLbs > 0 || Boolean(p.isPreorderEnabled);
          return (
            <article
              key={`${trackContext}-${p.id}`}
              className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50/50 transition hover:border-slate-300 hover:shadow-md"
            >
              <button
                type="button"
                onClick={() => {
                  void trackRecommendationEvent({
                    productId: p.id,
                    eventType: 'RECOMMENDATION_CLICK',
                    context: trackContext,
                    source: 'cart',
                  });
                  onOpenProduct(p.id);
                }}
                className="flex flex-1 flex-col text-left"
              >
                <div className="relative flex h-28 items-center justify-center bg-white p-3 sm:h-32">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={p.name}
                      className="max-h-full max-w-full object-contain transition group-hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="h-full w-full rounded-lg bg-slate-100" />
                  )}
                </div>
                <div className="flex flex-1 flex-col px-3 pb-2 pt-2">
                  <p className="line-clamp-2 text-xs font-semibold leading-snug text-slate-900 sm:text-sm">
                    {p.name}
                  </p>
                  <p className="mt-1 text-sm font-bold text-[#00b853] tabular-nums">
                    {formatPrice(unitPrice)}
                  </p>
                </div>
              </button>
              <div className="px-3 pb-3">
                <button
                  type="button"
                  disabled={!canAdd}
                  onClick={() => onAddProduct(p)}
                  className={[
                    'w-full rounded-lg px-2 py-2 text-xs font-semibold transition',
                    canAdd
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'cursor-not-allowed bg-slate-100 text-slate-400',
                  ].join(' ')}
                >
                  {canAdd ? addToCartLabel : 'Out of stock'}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

type CartBundleRailProps = {
  title: string;
  subtitle?: string;
  packages: StorefrontPackage[];
  viewAllLabel: string;
  onViewAll: () => void;
  formatPrice: (amount: number) => string;
  addLabel: string;
  onAddPackage: (pkg: StorefrontPackage) => void;
  onOpenPackage: (slug: string) => void;
};

function CartBundleRail({
  title,
  subtitle,
  packages,
  viewAllLabel,
  onViewAll,
  formatPrice,
  addLabel,
  onAddPackage,
  onOpenPackage,
}: CartBundleRailProps) {
  if (packages.length === 0) return null;
  return (
    <section className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">{title}</h2>
          {subtitle ? (
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600">{subtitle}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-[#00b853]/40 hover:text-[#00b853]"
        >
          {viewAllLabel}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {packages.map((pkg) => {
          const hero = productImageSrc(pkg.heroImageUrl);
          return (
            <article
              key={pkg.id}
              className="flex overflow-hidden rounded-xl border border-slate-200 bg-slate-50/50 transition hover:border-slate-300 hover:shadow-md"
            >
              <button
                type="button"
                onClick={() => onOpenPackage(pkg.slug)}
                className="h-20 w-20 shrink-0 overflow-hidden bg-slate-900/90"
              >
                {hero ? (
                  <img src={hero} alt={pkg.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] font-bold uppercase text-white">
                    Pack
                  </div>
                )}
              </button>
              <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
                <div>
                  <p className="line-clamp-2 text-sm font-semibold text-slate-900">{pkg.name}</p>
                  <p className="mt-0.5 text-sm font-bold text-[#00b853]">{formatPrice(pkg.priceUsd)}</p>
                </div>
                <button
                  type="button"
                  disabled={!isPackageAvailable(pkg)}
                  onClick={() => onAddPackage(pkg)}
                  className="mt-2 w-fit rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {addLabel}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default function CartPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, auth } = useAuth();
  const isCustomer = isAuthenticated && auth.type === 'CUSTOMER';
  const {
    lines,
    removeProduct,
    removePackage,
    updateQty,
    updateProductPackaging,
    updatePackageQty,
    addLine,
  } = useCart();
  const { currency, rates, ratesMeta, formatPrice, formatMajorInCurrency } = useCurrency();

  const [qtyDraftByLineKey, setQtyDraftByLineKey] = useState<Record<string, string>>({});
  const [expandedPackageItemsByLineKey, setExpandedPackageItemsByLineKey] = useState<Record<string, boolean>>({});
  const [productsById, setProductsById] = useState<Record<string, Product>>({});
  const [packagesById, setPackagesById] = useState<Record<string, StorefrontPackage>>({});
  const [suggestedPackages, setSuggestedPackages] = useState<StorefrontPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailableIds, setUnavailableIds] = useState<string[]>([]);
  const [unavailablePackageIds, setUnavailablePackageIds] = useState<string[]>([]);
  const [cartRecs, setCartRecs] = useState<{
    disabled: boolean;
    sections: {
      similar: Product[];
      frequentlyBoughtTogether: Product[];
      completeOrder: Product[];
    };
  } | null>(null);
  const [homeBestSellers, setHomeBestSellers] = useState<Product[]>([]);
  const [catalogFallbackProducts, setCatalogFallbackProducts] = useState<Product[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const productIds = Array.from(
        new Set(lines.filter((l): l is Extract<CartLine, { kind: 'product' }> => l.kind === 'product').map((l) => l.productId)),
      );
      const packageIds = Array.from(
        new Set(lines.filter((l): l is Extract<CartLine, { kind: 'package' }> => l.kind === 'package').map((l) => l.packageId)),
      );
      if (productIds.length === 0 && packageIds.length === 0) {
        setProductsById({});
        setPackagesById({});
        setUnavailableIds([]);
        setUnavailablePackageIds([]);
        return;
      }
      setLoading(true);
      try {
        const prodEntries: Array<[string, Product]> = [];
        for (const id of productIds) {
          const productUrl = new URL(
            applyContentLocaleToUrl(
              `${API_BASE}/catalog/products/${id}`,
              i18n.resolvedLanguage || i18n.language,
            ),
          );
          productUrl.searchParams.set('currency', (currency || 'USD').toUpperCase());
          const res = await fetch(productUrl.toString());
          if (!res.ok) continue;
          const p = (await res.json()) as Product;
          prodEntries.push([id, p]);
        }
        const pkgEntries: Array<
          [
            string,
            {
              id: string;
              name: string;
              slug: string;
              heroImageUrl?: string | null;
              priceUsd: number;
              items?: Array<{
                qty: number;
                unit: 'LB' | 'KG' | 'PACK';
                product?: {
                  id: string;
                  name: string;
                  pricePerUnit?: number | null;
                  salePrice?: number | null;
                  lots?: { expiryDate?: string | null }[];
                } | null;
              }>;
            },
          ]
        > = [];
        const pkgBad: string[] = [];
        for (const id of packageIds) {
          const packageUrl = new URL(
            applyContentLocaleToUrl(
              `${API_BASE}/catalog/packages/by-id/${encodeURIComponent(id)}`,
              i18n.resolvedLanguage || i18n.language,
            ),
          );
          packageUrl.searchParams.set('currency', (currency || 'USD').toUpperCase());
          const res = await fetch(packageUrl.toString());
          if (!res.ok) {
            pkgBad.push(id);
            continue;
          }
          const p = (await res.json()) as {
            id: string;
            name: string;
            slug: string;
            heroImageUrl?: string | null;
            priceUsd: number | string;
            items?: Array<{
              qty: number;
              unit: 'LB' | 'KG' | 'PACK';
              product?: {
                id: string;
                name: string;
                pricePerUnit?: number | null;
                salePrice?: number | null;
                lots?: { expiryDate?: string | null }[];
              } | null;
            }>;
          };
          pkgEntries.push([
            id,
            {
              id: p.id,
              name: p.name,
              slug: p.slug,
              heroImageUrl: p.heroImageUrl,
              priceUsd: Number(p.priceUsd),
              items: p.items,
            },
          ]);
        }
        if (!cancelled) {
          const productsMap = Object.fromEntries(prodEntries);
          setProductsById(productsMap);
          setPackagesById(Object.fromEntries(pkgEntries));
          setUnavailableIds(productIds.filter((id) => !(id in productsMap)));
          setUnavailablePackageIds(pkgBad);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency, lines, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const packagesUrl = new URL(
          applyContentLocaleToUrl(
            `${API_BASE}/catalog/packages`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        packagesUrl.searchParams.set('currency', (currency || 'USD').toUpperCase());
        const res = await fetch(packagesUrl.toString());
        if (!res.ok) return;
        const data = (await res.json()) as Array<StorefrontPackage & { priceUsd: number | string }>;
        if (cancelled || !Array.isArray(data)) return;
        const normalized = data
          .map((pkg) => ({
            ...pkg,
            priceUsd: Number(pkg.priceUsd ?? 0),
          }))
          .filter((pkg) => pkg.id && pkg.slug);
        setSuggestedPackages(normalized);
      } catch {
        if (!cancelled) setSuggestedPackages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ids = Array.from(
        new Set(
          lines
            .filter((l): l is Extract<CartLine, { kind: 'product' }> => l.kind === 'product')
            .map((l) => l.productId),
        ),
      );
      if (!ids.length) {
        setCartRecs(null);
        return;
      }
      try {
        const url = new URL(`${API_BASE}/public/recommendations/cart`);
        url.searchParams.set('productIds', ids.join(','));
        url.searchParams.set('sessionId', getRecommendationSessionId());
        const res = await fetch(
          applyContentLocaleToUrl(
            url.toString(),
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCartRecs(data);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency, lines, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const url = new URL(
          applyContentLocaleToUrl(
            `${API_BASE}/public/recommendations/home`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        url.searchParams.set('sessionId', getRecommendationSessionId());
        const res = await fetch(url.toString());
        if (!res.ok) {
          if (!cancelled) setHomeBestSellers([]);
          return;
        }
        const data = (await res.json()) as {
          disabled?: boolean;
          sections?: { bestSellers?: Product[] };
        };
        if (!cancelled) {
          const best = Array.isArray(data?.sections?.bestSellers) ? data.sections.bestSellers : [];
          setHomeBestSellers(best);
        }
      } catch {
        if (!cancelled) setHomeBestSellers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const url = new URL(`${API_BASE}/catalog/products`);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', '12');
        url.searchParams.set('activeOnly', 'true');
        url.searchParams.set('allowOutOfStock', 'true');
        const res = await fetch(
          applyContentLocaleToUrl(url.toString(), i18n.resolvedLanguage || i18n.language),
        );
        if (!res.ok) {
          if (!cancelled) setCatalogFallbackProducts([]);
          return;
        }
        const data = (await res.json()) as { items?: Product[] };
        if (!cancelled) {
          setCatalogFallbackProducts(Array.isArray(data?.items) ? data.items : []);
        }
      } catch {
        if (!cancelled) setCatalogFallbackProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    const keys = new Set(lines.map((l) => cartLineKey(l)));
    setQtyDraftByLineKey((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const k of Object.keys(next)) {
        if (!keys.has(k)) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setExpandedPackageItemsByLineKey((prev) => {
      const next: Record<string, boolean> = {};
      let changed = false;
      for (const [k, v] of Object.entries(prev)) {
        if (keys.has(k)) {
          next[k] = v;
        } else {
          changed = true;
        }
      }
      if (!changed && Object.keys(next).length === Object.keys(prev).length) return prev;
      return next;
    });
  }, [lines]);

  const totalsUsd = useMemo(() => {
    let full = 0;
    let dueNow = 0;
    for (const l of lines) {
      const lk = cartLineKey(l);
      if (l.kind === 'product') {
        const p = productsById[l.productId];
        if (!p) continue;
        const unitPrice = Number(p.salePrice ?? p.pricePerUnit);
        const qtyDraftParsed = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
          ? parseCartQuantityInput(qtyDraftByLineKey[lk])
          : null;
        const qtyForSubtotal =
          qtyDraftParsed != null && qtyDraftParsed > 0 ? qtyDraftParsed : l.qty;
        const lineFull = unitPrice * qtyForSubtotal;
        full += lineFull;
        if (p.isPreorderEnabled) {
          const raw = Number(p.preorderDepositValue ?? 0);
          const kind = String(p.preorderDepositType ?? 'PERCENT');
          const lineDueNow =
            kind === 'FIXED_USD'
              ? Math.max(0, raw) * qtyForSubtotal
              : lineFull * (Math.max(0, raw) / 100);
          dueNow += lineDueNow;
        } else {
          dueNow += lineFull;
        }
      } else {
        const pkg = packagesById[l.packageId];
        if (!pkg) continue;
        const packageDraftRaw = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
          ? qtyDraftByLineKey[lk]
          : null;
        const packageDraftParsed =
          packageDraftRaw != null ? parseInt(packageDraftRaw.trim(), 10) : NaN;
        const qtyForSubtotal =
          Number.isFinite(packageDraftParsed) && packageDraftParsed >= 1
            ? packageDraftParsed
            : Math.max(1, Math.floor(l.qty));
        const lineFull = pkg.priceUsd * qtyForSubtotal;
        full += lineFull;
        dueNow += lineFull;
      }
    }
    return { full, dueNow };
  }, [lines, productsById, packagesById, qtyDraftByLineKey]);

  const totalsDisplayMajor = useMemo(() => {
    const fxEnabled = ratesMeta.enabled !== false;
    if (!fxEnabled) {
      return {
        full: totalsUsd.full,
        dueNow: totalsUsd.dueNow,
        code: 'USD',
        hasPreorder: lines.some((l) => l.kind === 'product' && Boolean(productsById[l.productId]?.isPreorderEnabled)),
      };
    }
    const code = (currency || 'USD').toUpperCase();
    const rate = code === 'USD' ? 1 : Number(rates[code] ?? 0);
    if (!Number.isFinite(rate) || rate <= 0) {
      return { full: totalsUsd.full, dueNow: totalsUsd.dueNow, code };
    }
    const markupFactor =
      code === 'USD' ? 1 : 1 + Number(ratesMeta.markupPct ?? 0) / 100;
    const convert = (usd: number) =>
      applyFxRounding(usd * rate * markupFactor, ratesMeta.roundingProfile);
    let full = 0;
    let dueNow = 0;
    let hasPreorder = false;
    for (const l of lines) {
      const lk = cartLineKey(l);
      if (l.kind === 'product') {
        const p = productsById[l.productId];
        if (!p) continue;
        const unitPrice = Number(p.salePrice ?? p.pricePerUnit);
        const qtyDraftParsed = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
          ? parseCartQuantityInput(qtyDraftByLineKey[lk])
          : null;
        const qty = qtyDraftParsed != null && qtyDraftParsed > 0 ? qtyDraftParsed : l.qty;
        const unitMajor = convert(unitPrice);
        const lineFullMajor = unitMajor * qty;
        const isPreorder = Boolean(p.isPreorderEnabled);
        if (isPreorder) hasPreorder = true;
        const lineDueUsd = isPreorder
          ? String(p.preorderDepositType ?? 'PERCENT') === 'FIXED_USD'
            ? Math.max(0, Number(p.preorderDepositValue ?? 0)) * qty
            : (unitPrice * qty) * (Math.max(0, Number(p.preorderDepositValue ?? 0)) / 100)
          : unitPrice * qty;
        full += lineFullMajor;
        if (isPreorder && String(p.preorderDepositType ?? 'PERCENT') !== 'FIXED_USD') {
          // Keep percent deposits mathematically tied to displayed line amount.
          dueNow += lineFullMajor * (Math.max(0, Number(p.preorderDepositValue ?? 0)) / 100);
        } else if (!isPreorder) {
          // Non-preorder line should always be fully due now.
          dueNow += lineFullMajor;
        } else {
          dueNow += convert(lineDueUsd);
        }
      } else {
        const pkg = packagesById[l.packageId];
        if (!pkg) continue;
        const packageDraftRaw = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
          ? qtyDraftByLineKey[lk]
          : null;
        const packageDraftParsed =
          packageDraftRaw != null ? parseInt(packageDraftRaw.trim(), 10) : NaN;
        const qty =
          Number.isFinite(packageDraftParsed) && packageDraftParsed >= 1
            ? packageDraftParsed
            : Math.max(1, Math.floor(l.qty));
        const unitMajor = convert(pkg.priceUsd);
        const lineMajor = unitMajor * qty;
        full += lineMajor;
        dueNow += lineMajor;
      }
    }
    return { full, dueNow, code, hasPreorder };
  }, [
    currency,
    rates,
    ratesMeta.enabled,
    ratesMeta.markupPct,
    ratesMeta.roundingProfile,
    lines,
    productsById,
    packagesById,
    qtyDraftByLineKey,
    totalsUsd.dueNow,
    totalsUsd.full,
  ]);
  const productShortageByLineKey = useMemo(() => {
    const out = new Map<string, { requiredLbs: number; availableLbs: number; baseUnit: 'LB' | 'KG' }>();
    for (const l of lines) {
      if (l.kind !== 'product') continue;
      const p = productsById[l.productId];
      if (!p || p.currentStockLbs == null) continue;
      if (p.isPreorderEnabled) continue;
      const requiredLbs = toLbs(Number(l.qty || 0), l.unit);
      const availableLbs = Number(p.currentStockLbs || 0);
      if (availableLbs + 1e-9 < requiredLbs) {
        out.set(cartLineKey(l), {
          requiredLbs,
          availableLbs,
          baseUnit: p.baseUnit,
        });
      }
    }
    return out;
  }, [lines, productsById]);

  const packageShortageByLineKey = useMemo(() => {
    const out = new Map<string, { requestedBundles: number; maxBundles: number }>();
    for (const l of lines) {
      if (l.kind !== 'package') continue;
      const pkg = packagesById[l.packageId];
      if (!pkg?.items?.length) continue;
      let maxBundles = Number.POSITIVE_INFINITY;
      let hasComputableItem = false;
      for (const row of pkg.items) {
        const stockLbs = Number(row.product?.currentStockLbs ?? 0);
        const needPerBundleLbs = packageItemNeedLbs(Number(row.qty || 0), row.unit);
        if (!Number.isFinite(needPerBundleLbs) || needPerBundleLbs <= 0) continue;
        hasComputableItem = true;
        maxBundles = Math.min(maxBundles, Math.floor((stockLbs + 1e-9) / needPerBundleLbs));
      }
      if (!hasComputableItem || !Number.isFinite(maxBundles)) continue;
      const requestedBundles = Math.max(1, Math.floor(Number(l.qty || 0)));
      if (requestedBundles > maxBundles) {
        out.set(cartLineKey(l), { requestedBundles, maxBundles: Math.max(0, maxBundles) });
      }
    }
    return out;
  }, [lines, packagesById]);

  const preorderLimitViolationByLineKey = useMemo(() => {
    const out = new Map<
      string,
      { qtyLbs: number; maxLbs: number; productName: string; lineUnit: 'LB' | 'KG' }
    >();
    for (const l of lines) {
      if (l.kind !== 'product') continue;
      const p = productsById[l.productId];
      if (!p?.isPreorderEnabled) continue;
      const maxLbs = Number(p.preorderMaxQtyLbs ?? 0);
      if (!Number.isFinite(maxLbs) || maxLbs <= 0) continue;
      const lk = cartLineKey(l);
      const qtyDraftParsed = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
        ? parseCartQuantityInput(qtyDraftByLineKey[lk])
        : null;
      const qty =
        qtyDraftParsed != null && qtyDraftParsed > 0 ? qtyDraftParsed : l.qty;
      const qtyLbs = toLbs(qty, l.unit);
      if (qtyLbs - maxLbs > 1e-6) {
        out.set(lk, {
          qtyLbs,
          maxLbs,
          productName: p.name || l.productId,
          lineUnit: l.unit,
        });
      }
    }
    return out;
  }, [lines, productsById, qtyDraftByLineKey]);

  const hasStockShortage = productShortageByLineKey.size > 0 || packageShortageByLineKey.size > 0;
  const hasPreorderLimitViolation = preorderLimitViolationByLineKey.size > 0;
  const checkoutBlocked = hasStockShortage || hasPreorderLimitViolation;

  const suggestedPackagesVisible = useMemo(() => {
    const inCart = new Set(
      lines
        .filter((l): l is Extract<CartLine, { kind: 'package' }> => l.kind === 'package')
        .map((l) => l.packageId),
    );
    return suggestedPackages
      .filter((pkg) => !inCart.has(pkg.id))
      .slice(0, 4);
  }, [lines, suggestedPackages]);
  const compactProductSuggestions = useMemo(() => {
    if (!cartRecs) return homeBestSellers.slice(0, 6);
    if (cartRecs?.disabled) return homeBestSellers.slice(0, 6);
    const merged = [
      ...(cartRecs?.sections.similar || []),
      ...(cartRecs?.sections.frequentlyBoughtTogether || []),
      ...(cartRecs?.sections.completeOrder || []),
    ];
    const seen = new Set<string>();
    const out: Product[] = [];
    for (const p of merged) {
      if (!p?.id || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
      if (out.length >= 6) break;
    }
    return out.length > 0 ? out : homeBestSellers.slice(0, 6);
  }, [cartRecs, homeBestSellers]);
  const inCartProductIds = useMemo(
    () =>
      new Set(
        lines
          .filter((l): l is Extract<CartLine, { kind: 'product' }> => l.kind === 'product')
          .map((l) => l.productId),
      ),
    [lines],
  );

  /** Store-wide bestsellers (same source as home), never scoped to one department. */
  const bestsellerRailProducts = useMemo(() => {
    const out: Product[] = [];
    for (const p of homeBestSellers) {
      if (!p?.id || inCartProductIds.has(p.id)) continue;
      out.push(p);
      if (out.length >= 8) break;
    }
    return out;
  }, [homeBestSellers, inCartProductIds]);

  const bestsellerRailIdSet = useMemo(
    () => new Set(bestsellerRailProducts.map((p) => p.id)),
    [bestsellerRailProducts],
  );

  /** Complementary picks: cart-intelligent suggestions minus bestseller rail (no duplicate rows). */
  const morePicksProducts = useMemo(() => {
    const out: Product[] = [];
    for (const p of compactProductSuggestions) {
      if (!p?.id || inCartProductIds.has(p.id) || bestsellerRailIdSet.has(p.id)) continue;
      out.push(p);
      if (out.length >= 6) break;
    }
    return out;
  }, [compactProductSuggestions, inCartProductIds, bestsellerRailIdSet]);

  const filterRetentionProducts = useMemo(
    () => (items: Product[], limit = 8) => {
      const out: Product[] = [];
      for (const p of items) {
        if (!p?.id || inCartProductIds.has(p.id)) continue;
        out.push(p);
        if (out.length >= limit) break;
      }
      return out;
    },
    [inCartProductIds],
  );

  const frequentlyBoughtProducts = useMemo(
    () => filterRetentionProducts(cartRecs?.sections.frequentlyBoughtTogether || [], 8),
    [cartRecs?.sections.frequentlyBoughtTogether, filterRetentionProducts],
  );

  const similarProducts = useMemo(
    () => filterRetentionProducts(cartRecs?.sections.similar || [], 8),
    [cartRecs?.sections.similar, filterRetentionProducts],
  );

  const completeOrderProducts = useMemo(
    () => filterRetentionProducts(cartRecs?.sections.completeOrder || [], 8),
    [cartRecs?.sections.completeOrder, filterRetentionProducts],
  );

  const exploreProducts = useMemo(() => {
    if (bestsellerRailProducts.length > 0) return bestsellerRailProducts;
    if (morePicksProducts.length > 0) return morePicksProducts;
    return filterRetentionProducts(catalogFallbackProducts, 10);
  }, [
    bestsellerRailProducts,
    morePicksProducts,
    catalogFallbackProducts,
    filterRetentionProducts,
  ]);

  const cartLineCount = lines.length;

  const addRecommendedProduct = (p: Product, context: string) => {
    if (!lines.some((l) => l.kind === 'product' && l.productId === p.id)) {
      addLine({ productId: p.id, qty: 1, unit: p.baseUnit });
    } else {
      const current = lines.find((l) => l.kind === 'product' && l.productId === p.id);
      updateQty(p.id, Number((current?.qty || 0) + 1));
    }
    void trackRecommendationEvent({
      productId: p.id,
      eventType: 'ADD_TO_CART',
      context,
      source: 'cart',
    });
  };

  const cartKey = useMemo(
    () =>
      lines
        .map((l) =>
          l.kind === 'package'
            ? `k:${l.packageId}:${l.qty}`
            : `p:${l.productId}:${l.qty}:${l.unit}`,
        )
        .join('|'),
    [lines],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        CHECKOUT_PRICING_SNAPSHOT_KEY,
        JSON.stringify({
          cartKey,
          merchandiseDueNowUsd: Number(totalsUsd.dueNow.toFixed(2)),
          savedAt: Date.now(),
        }),
      );
    } catch {
      // ignore storage failures
    }
  }, [cartKey, totalsUsd.dueNow]);

  const goCheckout = () => {
    if (checkoutBlocked) return;
    if (!isCustomer) {
      navigate(buildLoginUrl(getReturnPathForAuthRedirect()));
      return;
    }
    navigate('/checkout');
  };

  return (
    <StorefrontLayout mainClassName="bg-slate-50/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 pb-36 sm:px-6 sm:py-8 lg:px-8 lg:pb-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#00b853]">
              {t('storefront.cart_page_kicker', { defaultValue: 'Your basket' })}
            </p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t('storefront.cart_title')}
              {cartLineCount > 0 ? (
                <span className="ml-2 text-lg font-semibold text-slate-500 sm:text-xl">
                  ({t('storefront.cart_items_count', {
                    defaultValue: '{{count}} item(s)',
                    count: cartLineCount,
                  })})
                </span>
              ) : null}
            </h1>
            {cartLineCount > 0 ? (
              <p className="mt-1 max-w-xl text-sm text-slate-600">
                {t('storefront.cart_page_subtitle', {
                  defaultValue:
                    'Review your items below — add a few more favorites before checkout.',
                })}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#00b853]/40 hover:text-[#00b853]"
          >
            {t('storefront.continue_shopping')}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {(unavailableIds.length > 0 || unavailablePackageIds.length > 0) && (
          <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
            {t(
              'storefront.cart_unavailable_warning',
              'Some items in your cart are no longer available. Please remove them to continue.',
            )}
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:items-start">
          <div className="min-w-0 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                  {t('storefront.cart_items_title', { defaultValue: 'Cart items' })}
                </h2>
                {cartLineCount > 0 ? (
                  <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                    {cartLineCount}
                  </span>
                ) : null}
              </div>
              {loading && (
                <p className="text-sm text-slate-600">{t('common.loading')}</p>
              )}
              {lines.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-10 text-center">
                  <Package className="mx-auto h-10 w-10 text-slate-400" aria-hidden />
                  <p className="mt-3 text-base font-semibold text-slate-900">
                    {t('storefront.cart_empty')}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    {t('storefront.cart_empty_hint', {
                      defaultValue: 'Browse our catalog and add items to get started.',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/products')}
                    className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#00b853] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#009946]"
                  >
                    {t('storefront.continue_shopping')}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
              {lines.map((l) => {
                const lk = cartLineKey(l);
                if (l.kind === 'package') {
                  const pkg = packagesById[l.packageId];
                  const unitPrice = pkg?.priceUsd ?? 0;
                  const compareTotalPerBundle = (pkg?.items || []).reduce((sum, row) => {
                    const unit = Number(row.product?.salePrice ?? row.product?.pricePerUnit ?? 0);
                    const qty = Number(row.qty ?? 0);
                    if (!Number.isFinite(unit) || unit <= 0) return sum;
                    if (!Number.isFinite(qty) || qty <= 0) return sum;
                    return sum + unit * qty;
                  }, 0);
                  const hero =
                    pkg?.heroImageUrl && pkg.heroImageUrl.startsWith('http')
                      ? pkg.heroImageUrl
                      : pkg?.heroImageUrl
                        ? `${API_BASE}${pkg.heroImageUrl}`
                        : undefined;
                  const qtyInt = Math.max(1, Math.floor(l.qty));
                  const linePrice = unitPrice * qtyInt;
                  const compareLineTotal = compareTotalPerBundle * qtyInt;
                  const isExpanded = !!expandedPackageItemsByLineKey[lk];
                  const packageItems = pkg?.items || [];
                  const visibleItems = isExpanded ? packageItems : packageItems.slice(0, 1);
                  const qtyDisplayValue = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
                    ? qtyDraftByLineKey[lk]
                    : String(qtyInt);
                  const packageShortage = packageShortageByLineKey.get(lk);
                  return (
                    <div key={lk} className="rounded-xl border border-slate-200 bg-white p-2.5 sm:p-5">
                      <div className="flex items-start gap-2.5 sm:flex-row sm:items-start sm:gap-4">
                        <div className="flex min-w-0 flex-1 items-start gap-2.5">
                          <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-900/90 ring-1 ring-slate-200">
                          {hero ? (
                            <img
                              src={hero}
                              alt={pkg?.name ?? 'Package'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-slate-800 text-[10px] font-semibold uppercase text-white">
                              {t('storefront.package_badge', { defaultValue: 'Pack' })}
                            </div>
                          )}
                          </div>
                          <div className="min-w-0 flex-1 space-y-1.5 sm:space-y-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="line-clamp-2 break-words text-[19px] font-semibold leading-tight text-slate-900 sm:line-clamp-none sm:text-base">
                              {pkg?.name ?? l.packageId}
                            </div>
                            <button
                              type="button"
                              onClick={() => removePackage(l.packageId)}
                              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-600 sm:hidden"
                              aria-label={t('common.remove')}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="hidden flex-wrap items-end gap-x-2 gap-y-1 sm:flex sm:gap-x-3">
                            <span className="text-[18px] font-extrabold leading-none text-[#69b867] sm:text-lg">
                              {formatPrice(linePrice)}
                            </span>
                            {compareLineTotal > 0 ? (
                              <span className="text-sm font-semibold leading-none text-rose-500 line-through">
                                {formatPrice(compareLineTotal)}
                              </span>
                            ) : null}
                          </div>
                          <p className="hidden text-[10px] font-medium text-slate-500 sm:block sm:text-[11px]">
                            {t('storefront.package_line_hint', { defaultValue: 'Bundle price (vs item-by-item total)' })}
                          </p>
                          <ul className={`space-y-2 ${isExpanded ? 'block' : 'hidden sm:block'}`}>
                            {visibleItems.map((row, idx) => (
                              <li
                                key={`${lk}-pkg-item-${idx}`}
                                className="rounded-md border border-slate-200 bg-slate-50/80 px-2.5 py-1.5 sm:px-3 sm:py-2"
                              >
                                <p className="break-words text-xs font-extrabold text-slate-800 sm:text-sm">
                                  {Number(row.qty || 0) * qtyInt} {row.unit}{' '}
                                  {row.product?.name ||
                                    t('storefront.package_item_fallback', { defaultValue: 'Item' })}
                                </p>
                                <p className="mt-1 text-[10px] font-semibold text-slate-600 sm:text-[11px]">
                                  {t('storefront.earliest_best_before_stock', {
                                    defaultValue: 'Earliest best-before on current stock:',
                                  })}{' '}
                                  <span className="font-bold text-amber-700">
                                    {row.product?.lots?.[0]?.expiryDate
                                      ? new Date(
                                          row.product.lots[0].expiryDate as string,
                                        ).toLocaleDateString()
                                      : t('common.not_available', { defaultValue: 'N/A' })}
                                  </span>
                                </p>
                              </li>
                            ))}
                          </ul>
                          {packageItems.length > 1 ? (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedPackageItemsByLineKey((prev) => ({
                                  ...prev,
                                  [lk]: !isExpanded,
                                }))
                              }
                              className="hidden items-center gap-1.5 text-[11px] font-semibold text-slate-600 hover:text-slate-800 sm:inline-flex sm:text-[12px]"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                                  {t('storefront.package_items_show_less', {
                                    defaultValue: 'Show less',
                                  })}
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                                  {t('storefront.package_items_show_all', {
                                    defaultValue: 'View {{count}} more item(s)',
                                    count: Math.max(0, packageItems.length - 1),
                                  })}
                                </>
                              )}
                            </button>
                          ) : null}
                          {packageShortage && (
                            <p className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-800">
                              {t('storefront.cart_package_stock_remaining_line', {
                                defaultValue: 'Remaining bundles: {{available}} | Requested: {{required}}',
                                available: packageShortage.maxBundles,
                                required: packageShortage.requestedBundles,
                              })}
                            </p>
                          )}
                          <div className="mt-2 flex items-center justify-between gap-2 sm:hidden">
                            <span className="text-[18px] font-extrabold leading-none text-[#69b867]">
                              {formatPrice(linePrice)}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  const nextQty = Math.max(1, Math.floor(l.qty) - 1);
                                  updatePackageQty(l.packageId, nextQty);
                                }}
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-transparent bg-[#8bd27f] text-xs font-bold text-white hover:bg-[#79c96f]"
                                aria-label={t('common.decrease', { defaultValue: 'Decrease quantity' })}
                              >
                                -
                              </button>
                              <div className="relative">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  aria-label={t('storefront.cart_package_qty_aria', { defaultValue: 'Package quantity' })}
                                  value={qtyDisplayValue}
                                  onChange={(e) =>
                                    setQtyDraftByLineKey((prev) => ({ ...prev, [lk]: e.target.value }))
                                  }
                                  onBlur={(e) => {
                                    if (!Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)) return;
                                    const raw = e.target.value.trim();
                                    const n = parseInt(raw, 10);
                                    setQtyDraftByLineKey((prev) => {
                                      const next = { ...prev };
                                      delete next[lk];
                                      return next;
                                    });
                                    if (!Number.isFinite(n) || n < 1) return;
                                    updatePackageQty(l.packageId, n);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                  }}
                                  className="h-7 w-16 rounded-md border border-slate-300 bg-white pl-2 pr-6 py-1 text-center text-sm tabular-nums outline-none focus:border-[#00b853] focus:ring-2 focus:ring-[#00b853]/25"
                                />
                                <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase text-slate-500">
                                  pkg
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const nextQty = Math.max(1, Math.floor(l.qty) + 1);
                                  updatePackageQty(l.packageId, nextQty);
                                }}
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-transparent bg-[#8bd27f] text-xs font-bold text-white hover:bg-[#79c96f]"
                                aria-label={t('common.increase', { defaultValue: 'Increase quantity' })}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          {packageItems.length > 1 ? (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedPackageItemsByLineKey((prev) => ({
                                  ...prev,
                                  [lk]: !isExpanded,
                                }))
                              }
                              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 hover:text-slate-800 sm:hidden"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                                  {t('storefront.package_items_show_less', {
                                    defaultValue: 'Show less',
                                  })}
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                                  {t('storefront.package_items_show_all', {
                                    defaultValue: 'View {{count}} more item(s)',
                                    count: Math.max(0, packageItems.length - 1),
                                  })}
                                </>
                              )}
                            </button>
                          ) : null}
                        </div>
                        </div>
                        <div className="mt-2 hidden shrink-0 flex-col items-end gap-1.5 self-start sm:mt-0 sm:flex sm:w-auto sm:self-start sm:pt-0">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                const nextQty = Math.max(1, Math.floor(l.qty) - 1);
                                updatePackageQty(l.packageId, nextQty);
                              }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-transparent bg-[#8bd27f] text-xs font-bold text-white hover:bg-[#79c96f] sm:h-9 sm:w-9 sm:border-slate-300 sm:bg-transparent sm:text-base sm:text-slate-700 sm:hover:border-[#00b853] sm:hover:text-[#00b853]"
                              aria-label={t('common.decrease', { defaultValue: 'Decrease quantity' })}
                            >
                              -
                            </button>
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              aria-label={t('storefront.cart_package_qty_aria', { defaultValue: 'Package quantity' })}
                              value={qtyDisplayValue}
                              onChange={(e) =>
                                setQtyDraftByLineKey((prev) => ({ ...prev, [lk]: e.target.value }))
                              }
                              onBlur={(e) => {
                                if (!Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)) return;
                                const raw = e.target.value.trim();
                                const n = parseInt(raw, 10);
                                setQtyDraftByLineKey((prev) => {
                                  const next = { ...prev };
                                  delete next[lk];
                                  return next;
                                });
                                if (!Number.isFinite(n) || n < 1) return;
                                updatePackageQty(l.packageId, n);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                              }}
                              className="h-7 w-14 rounded-md border border-slate-300 px-2 py-1 text-center text-sm tabular-nums outline-none focus:border-[#00b853] focus:ring-2 focus:ring-[#00b853]/25 sm:h-9 sm:w-[5.5rem] sm:rounded-lg sm:px-3 sm:py-2"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const nextQty = Math.max(1, Math.floor(l.qty) + 1);
                                updatePackageQty(l.packageId, nextQty);
                              }}
                              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-transparent bg-[#8bd27f] text-xs font-bold text-white hover:bg-[#79c96f] sm:h-9 sm:w-9 sm:border-slate-300 sm:bg-transparent sm:text-base sm:text-slate-700 sm:hover:border-[#00b853] sm:hover:text-[#00b853]"
                              aria-label={t('common.increase', { defaultValue: 'Increase quantity' })}
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => removePackage(l.packageId)}
                            className="hidden h-8 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[10px] font-semibold text-rose-600 hover:border-rose-300 hover:bg-rose-100 hover:text-rose-700 sm:inline-flex sm:h-auto sm:px-3 sm:py-2 sm:text-xs"
                          >
                            {t('common.remove')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
                const p = productsById[l.productId];
                const packagingOptions = p?.packagingOptions || [];
                const usesPackaging = packagingOptions.length > 0;
                const packagingSelections =
                  l.packagingSelections?.length
                    ? l.packagingSelections
                    : usesPackaging
                      ? defaultSelections(packagingOptions, 1)
                      : [];
                const unitPrice = p ? Number(p.salePrice ?? p.pricePerUnit) : 0;
                const imageSrc =
                  p?.imageUrl && p.imageUrl.startsWith('http')
                    ? p.imageUrl
                    : p?.imageUrl
                      ? `${API_BASE}${p.imageUrl}`
                      : undefined;
                const baseUnit = p?.baseUnit ?? l.unit;
                const unitLc = baseUnit.toLowerCase();
                const stockLbs = p?.currentStockLbs;
                const stockLine =
                  p && stockLbs != null && stockLbs > 0
                    ? t(
                        baseUnit === 'KG' ? 'storefront.cart_stock_line_kg' : 'storefront.cart_stock_line_lb',
                        {
                          qty: new Intl.NumberFormat(undefined, {
                            maximumFractionDigits: baseUnit === 'KG' ? 2 : 1,
                            minimumFractionDigits: 0,
                          }).format(stockQtyInBaseUnit(stockLbs, baseUnit)),
                        },
                      )
                    : null;
                const shelfHint = p ? shelfLifeMessage(p, t) : null;
                const qtyDisplayValue = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
                  ? qtyDraftByLineKey[lk]
                  : formatQtyForInput(l.qty);
                const qtyDraftParsed = Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)
                  ? parseCartQuantityInput(qtyDraftByLineKey[lk])
                  : null;
                const qtyForLineTotal =
                  qtyDraftParsed != null && qtyDraftParsed > 0 ? qtyDraftParsed : l.qty;
                const unitPriceDisplayMajor = (() => {
                  const code = (currency || 'USD').toUpperCase();
                  const rate = code === 'USD' ? 1 : Number(rates[code] ?? 0);
                  if (!Number.isFinite(rate) || rate <= 0) return unitPrice;
                  const markupFactor =
                    code === 'USD' ? 1 : 1 + Number(ratesMeta.markupPct ?? 0) / 100;
                  return applyFxRounding(
                    unitPrice * rate * markupFactor,
                    ratesMeta.roundingProfile,
                  );
                })();
                const lineTotalDisplayMajor = unitPriceDisplayMajor * qtyForLineTotal;
                const shortage = productShortageByLineKey.get(lk);
                const preorderViolation = preorderLimitViolationByLineKey.get(lk);
                const isPreorderLine = Boolean(p?.isPreorderEnabled);
                const preorderEtaLabel = p?.preorderEta
                  ? new Date(p.preorderEta).toLocaleDateString()
                  : t('common.not_available', { defaultValue: 'TBA' });
                const preorderDepositLabel = p
                  ? p.preorderDepositType === 'FIXED_USD'
                    ? `${formatPrice(Number(p.preorderDepositValue ?? 0))} / ${unitLc}`
                    : `${Number(p.preorderDepositValue ?? 0)}%`
                  : null;
                const preorderMaxQtyLabel =
                  p?.preorderMaxQtyLbs != null && Number(p.preorderMaxQtyLbs) > 0
                    ? `${Number(p.preorderMaxQtyLbs).toFixed(1)} lbs`
                    : null;

                return (
                  <>
                    <div key={`${lk}-mobile`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_0_rgba(15,23,42,0.03)] sm:hidden">
                      <div className="flex items-start gap-3">
                        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                          {imageSrc ? (
                            // eslint-disable-next-line jsx-a11y/img-redundant-alt
                            <img src={imageSrc} alt={p?.name ?? t('storefront.product_image_alt')} className="h-full w-full object-contain" />
                          ) : (
                            <div className="h-full w-full bg-slate-200" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="line-clamp-2 text-[19px] font-semibold leading-tight text-slate-900">
                              {p?.name ?? l.productId}
                            </p>
                            {isPreorderLine ? (
                              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                                Pre-order
                              </span>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => removeProduct(l.productId)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-600"
                              aria-label={t('common.remove')}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {stockLine ? (
                            <div className="mt-1 flex items-center gap-1.5 text-[12px] font-medium text-slate-500">
                              <span>{stockLine}</span>
                              <div className="group relative">
                                <button
                                  type="button"
                                  className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-[9px] font-medium text-emerald-700"
                                  aria-label={shelfHint || t('storefront.cart_best_before', { date: t('common.not_available', { defaultValue: 'N/A' }) })}
                                >
                                  i
                                </button>
                                {shelfHint ? (
                                  <div className="pointer-events-none absolute right-0 top-6 z-10 hidden w-48 rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white shadow-lg group-hover:block group-focus-within:block">
                                    {shelfHint}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1 h-5" />
                          )}
                          {isPreorderLine ? (
                            <div className="mt-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1.5 text-[11px] text-indigo-800">
                              <p className="font-semibold">Pre-order item</p>
                              <p>Estimated Next arrival: {preorderEtaLabel}</p>
                              {preorderDepositLabel ? (
                                <p>Deposit at checkout: {preorderDepositLabel}</p>
                              ) : null}
                              {preorderMaxQtyLabel ? <p>Pre-order limit: {preorderMaxQtyLabel}</p> : null}
                            </div>
                          ) : null}
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <span className="text-[18px] font-extrabold leading-none text-[#69b867]">
                              {formatMajorInCurrency(
                                lineTotalDisplayMajor,
                                (currency || 'USD').toUpperCase(),
                              )}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  const nextQty = Math.max(0.5, roundToStep(l.qty - 0.5, 1));
                                  updateQty(l.productId, nextQty);
                                }}
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#8bd27f] text-xs font-bold text-white"
                                aria-label={t('common.decrease', { defaultValue: 'Decrease quantity' })}
                              >
                                -
                              </button>
                              <div className="relative">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  autoComplete="off"
                                  aria-label={t('storefront.cart_qty_aria', { unit: unitLc })}
                                  value={qtyDisplayValue}
                                  onChange={(e) =>
                                    setQtyDraftByLineKey((prev) => ({ ...prev, [lk]: e.target.value }))
                                  }
                                  onBlur={(e) => {
                                    if (!Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)) return;
                                    const raw = e.target.value;
                                    const parsed = parseCartQuantityInput(raw);
                                    if (parsed === null || parsed < 0.5) {
                                      setQtyDraftByLineKey((prev) => {
                                        const next = { ...prev };
                                        delete next[lk];
                                        return next;
                                      });
                                      return;
                                    }
                                    let nextQty = Math.max(0.5, roundToStep(Math.round(parsed / 0.5) * 0.5, 1));
                                    if (p && !p.isPreorderEnabled && p.currentStockLbs != null && p.currentStockLbs > 0) {
                                      const maxInUnit = stockQtyInBaseUnit(p.currentStockLbs, p.baseUnit);
                                      if (nextQty > maxInUnit) {
                                        nextQty = Math.max(0.5, floorToStep(maxInUnit, 0.5));
                                      }
                                    }
                                    if (p?.isPreorderEnabled) {
                                      const cap = maxPreorderQtyInLineUnit(p.preorderMaxQtyLbs, l.unit);
                                      if (cap != null && nextQty > cap) nextQty = cap;
                                    }
                                    updateQty(l.productId, nextQty);
                                    setQtyDraftByLineKey((prev) => {
                                      const next = { ...prev };
                                      delete next[lk];
                                      return next;
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                  }}
                                  className="h-7 w-16 rounded-md border border-slate-300 bg-white pl-1.5 pr-5 py-1 text-center text-sm font-semibold tabular-nums text-slate-900 outline-none focus:border-[#00b853] focus:ring-2 focus:ring-[#00b853]/25"
                                />
                                <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase text-slate-500">
                                  {unitLc}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  let nextQty = roundToStep(l.qty + 0.5, 1);
                                  if (p && !p.isPreorderEnabled && p.currentStockLbs != null && p.currentStockLbs > 0) {
                                    const maxInUnit = stockQtyInBaseUnit(p.currentStockLbs, p.baseUnit);
                                    nextQty = Math.min(nextQty, Math.max(0.5, floorToStep(maxInUnit, 0.5)));
                                  }
                                  if (p?.isPreorderEnabled) {
                                    const cap = maxPreorderQtyInLineUnit(p.preorderMaxQtyLbs, l.unit);
                                    if (cap != null) nextQty = Math.min(nextQty, cap);
                                  }
                                  updateQty(l.productId, Math.max(0.5, nextQty));
                                }}
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#8bd27f] text-xs font-bold text-white"
                                aria-label={t('common.increase', { defaultValue: 'Increase quantity' })}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          {shortage ? (
                            <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-800">
                              {t('storefront.cart_stock_remaining_line', {
                                defaultValue: 'Remaining: {{available}} {{unit}} | Requested: {{required}} {{unit}}',
                                available: (shortage.baseUnit === 'KG'
                                  ? shortage.availableLbs * LB_PER_KG
                                  : shortage.availableLbs
                                ).toFixed(shortage.baseUnit === 'KG' ? 2 : 1),
                                required: (shortage.baseUnit === 'KG'
                                  ? shortage.requiredLbs * LB_PER_KG
                                  : shortage.requiredLbs
                                ).toFixed(shortage.baseUnit === 'KG' ? 2 : 1),
                                unit: shortage.baseUnit.toLowerCase(),
                              })}
                            </p>
                          ) : null}
                          {preorderViolation ? (
                            <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-800">
                              {t('storefront.cart_preorder_limit_exceeded', {
                                defaultValue:
                                  'Pre-order limit is {{max}} lbs total for this product. Reduce quantity (currently {{current}} lbs).',
                                max: preorderViolation.maxLbs.toFixed(1),
                                current: preorderViolation.qtyLbs.toFixed(1),
                              })}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div key={`${lk}-desktop`} className="hidden rounded-lg border border-slate-200 bg-white p-4 sm:block">
                      <div className="flex items-start gap-4">
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                          {imageSrc ? (
                            // eslint-disable-next-line jsx-a11y/img-redundant-alt
                            <img src={imageSrc} alt={p?.name ?? t('storefront.product_image_alt')} className="h-full w-full object-contain" />
                          ) : (
                            <div className="h-full w-full bg-slate-200" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="break-words text-sm font-semibold text-slate-900">{p?.name ?? l.productId}</div>
                          {isPreorderLine ? (
                            <span className="inline-flex w-fit rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                              Pre-order
                            </span>
                          ) : null}
                          <div className="text-xs text-slate-500">
                            {formatMajorInCurrency(
                              unitPriceDisplayMajor,
                              (currency || 'USD').toUpperCase(),
                            )}{' '}
                            / {unitLc}
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            {t('storefront.cart_line_total', {
                              defaultValue: 'Total: {{amount}}',
                              amount: formatMajorInCurrency(
                                lineTotalDisplayMajor,
                                (currency || 'USD').toUpperCase(),
                              ),
                            })}
                          </div>
                          {stockLine ? (
                            <div className="flex flex-wrap items-center gap-2 pt-0.5">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-900 ring-1 ring-emerald-200/90">
                                <Package className="h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden />
                                <span className="text-emerald-800">{t('storefront.cart_stock_badge')}</span>
                                <span className="font-medium text-emerald-700/90">· {stockLine}</span>
                              </span>
                            </div>
                          ) : null}
                          {shelfHint ? (
                            <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
                              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
                              <span>{shelfHint}</span>
                            </p>
                          ) : null}
                          {isPreorderLine ? (
                            <div className="rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-2 text-xs text-indigo-900">
                              <p>Estimated Next arrival: {preorderEtaLabel}</p>
                              {preorderDepositLabel ? (
                                <p>Deposit at checkout: {preorderDepositLabel}</p>
                              ) : null}
                              {preorderMaxQtyLabel ? <p>Pre-order limit: {preorderMaxQtyLabel}</p> : null}
                            </div>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2 self-start">
                          {usesPackaging ? (
                            <div className="w-full min-w-[240px]">
                              <ProductPackagingPicker
                                compact
                                options={packagingOptions}
                                unit={l.unit}
                                selections={packagingSelections}
                                maxTotalLbs={
                                  p && !p.isPreorderEnabled && p.currentStockLbs != null
                                    ? p.currentStockLbs
                                    : null
                                }
                                onChange={(next) =>
                                  updateProductPackaging(
                                    l.productId,
                                    next,
                                    l.unit,
                                    packagingOptions,
                                  )
                                }
                              />
                            </div>
                          ) : (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                const nextQty = Math.max(0.5, roundToStep(l.qty - 0.5, 1));
                                updateQty(l.productId, nextQty);
                              }}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-base font-bold text-slate-700 hover:border-[#00b853] hover:text-[#00b853]"
                              aria-label={t('common.decrease', { defaultValue: 'Decrease quantity' })}
                            >
                              -
                            </button>
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              aria-label={t('storefront.cart_qty_aria', { unit: unitLc })}
                              value={qtyDisplayValue}
                              onChange={(e) =>
                                setQtyDraftByLineKey((prev) => ({ ...prev, [lk]: e.target.value }))
                              }
                              onBlur={(e) => {
                                if (!Object.prototype.hasOwnProperty.call(qtyDraftByLineKey, lk)) return;
                                const raw = e.target.value;
                                const parsed = parseCartQuantityInput(raw);
                                if (parsed === null || parsed < 0.5) {
                                  setQtyDraftByLineKey((prev) => {
                                    const next = { ...prev };
                                    delete next[lk];
                                    return next;
                                  });
                                  return;
                                }
                                let nextQty = Math.max(0.5, roundToStep(Math.round(parsed / 0.5) * 0.5, 1));
                                if (p && !p.isPreorderEnabled && p.currentStockLbs != null && p.currentStockLbs > 0) {
                                  const maxInUnit = stockQtyInBaseUnit(p.currentStockLbs, p.baseUnit);
                                  if (nextQty > maxInUnit) {
                                    nextQty = Math.max(0.5, floorToStep(maxInUnit, 0.5));
                                  }
                                }
                                if (p?.isPreorderEnabled) {
                                  const cap = maxPreorderQtyInLineUnit(p.preorderMaxQtyLbs, l.unit);
                                  if (cap != null && nextQty > cap) nextQty = cap;
                                }
                                updateQty(l.productId, nextQty);
                                setQtyDraftByLineKey((prev) => {
                                  const next = { ...prev };
                                  delete next[lk];
                                  return next;
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                              }}
                              className="h-9 w-[5.5rem] rounded-lg border border-slate-300 px-3 py-2 text-center text-sm tabular-nums outline-none focus:border-[#00b853] focus:ring-2 focus:ring-[#00b853]/25"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                let nextQty = roundToStep(l.qty + 0.5, 1);
                                if (p && !p.isPreorderEnabled && p.currentStockLbs != null && p.currentStockLbs > 0) {
                                  const maxInUnit = stockQtyInBaseUnit(p.currentStockLbs, p.baseUnit);
                                  nextQty = Math.min(nextQty, Math.max(0.5, floorToStep(maxInUnit, 0.5)));
                                }
                                if (p?.isPreorderEnabled) {
                                  const cap = maxPreorderQtyInLineUnit(p.preorderMaxQtyLbs, l.unit);
                                  if (cap != null) nextQty = Math.min(nextQty, cap);
                                }
                                updateQty(l.productId, Math.max(0.5, nextQty));
                              }}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-base font-bold text-slate-700 hover:border-[#00b853] hover:text-[#00b853]"
                              aria-label={t('common.increase', { defaultValue: 'Increase quantity' })}
                            >
                              +
                            </button>
                          </div>
                          )}
                          {usesPackaging && packagingSelections.length > 0 ? (
                            <p className="text-[11px] text-slate-600">
                              {formatPackagingSummary(packagingSelections, packagingOptions)}
                            </p>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => removeProduct(l.productId)}
                            className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:border-rose-300 hover:bg-rose-100"
                          >
                            {t('common.remove')}
                          </button>
                        </div>
                      </div>
                      {preorderViolation ? (
                        <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-800">
                          {t('storefront.cart_preorder_limit_exceeded', {
                            defaultValue:
                              'Pre-order limit is {{max}} lbs total for this product. Reduce quantity (currently {{current}} lbs).',
                            max: preorderViolation.maxLbs.toFixed(1),
                            current: preorderViolation.qtyLbs.toFixed(1),
                          })}
                        </p>
                      ) : null}
                    </div>
                  </>
                );
              })}
                </div>
              )}
            </div>
          </div>

          {lines.length > 0 ? (
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-base font-bold text-slate-900">{t('storefront.summary')}</h3>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-slate-600">{t('storefront.subtotal')}</span>
                  <span className="text-lg font-bold tabular-nums text-slate-900">
                    {formatMajorInCurrency(totalsDisplayMajor.full, totalsDisplayMajor.code)}
                  </span>
                </div>
                {totalsDisplayMajor.hasPreorder &&
                Math.abs(totalsDisplayMajor.dueNow - totalsDisplayMajor.full) > 0.009 ? (
                  <>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="text-slate-500">Due now</span>
                      <span className="font-semibold text-indigo-700">
                        {formatMajorInCurrency(totalsDisplayMajor.dueNow, totalsDisplayMajor.code)}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Pre-order deposits are charged now; remaining balance is charged later.
                    </p>
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={goCheckout}
                  disabled={
                    unavailableIds.length > 0 ||
                    unavailablePackageIds.length > 0 ||
                    checkoutBlocked
                  }
                  className="mt-5 hidden w-full rounded-full bg-[#00b853] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#009946] disabled:cursor-not-allowed disabled:opacity-60 lg:block"
                >
                  {t('storefront.order_now', { defaultValue: 'Order Now' })}
                </button>
                {hasStockShortage ? (
                  <p className="mt-2 text-xs font-medium text-rose-700">
                    {t('storefront.cart_checkout_blocked_shortage', {
                      defaultValue: 'Checkout disabled: reduce quantities to available stock.',
                    })}
                  </p>
                ) : null}
                {hasPreorderLimitViolation ? (
                  <p className="mt-2 text-xs font-medium text-rose-700">
                    {t('storefront.cart_checkout_blocked_preorder_limit', {
                      defaultValue:
                        'Checkout disabled: a pre-order item exceeds its quantity limit. Reduce quantity to continue.',
                    })}
                  </p>
                ) : null}
                <div className="mt-5 space-y-2 border-t border-slate-100 pt-4">
                  <p className="flex items-start gap-2 text-xs text-slate-600">
                    <Shield className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                    {t('storefront.cart_trust_secure', {
                      defaultValue: 'Secure checkout powered by Stripe — we never store your card.',
                    })}
                  </p>
                  <p className="flex items-start gap-2 text-xs text-slate-600">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                    {t('storefront.cart_trust_fresh', {
                      defaultValue: 'Fresh stock with clear best-before dates on every line.',
                    })}
                  </p>
                </div>
              </div>
            </aside>
          ) : null}
        </div>

        {lines.length > 0 ? (
          <div className="mt-12 space-y-8 border-t border-slate-200 pt-10">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#00b853]" aria-hidden />
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                {t('storefront.cart_rec_rail_kicker', { defaultValue: 'Discover more' })}
              </p>
            </div>

            <CartRetentionRail
              title={t('storefront.cart_fbt_title', {
                defaultValue: 'Frequently bought together',
              })}
              subtitle={t('storefront.cart_fbt_subtitle', {
                defaultValue: 'Customers who bought items in your cart also added these.',
              })}
              products={frequentlyBoughtProducts}
              viewAllLabel={t('storefront.cart_suggested_products_view_all', {
                defaultValue: 'Shop all',
              })}
              onViewAll={() => navigate('/products')}
              trackContext="cart_fbt"
              formatPrice={formatPrice}
              addToCartLabel={t('storefront.cart_rec_add_short', { defaultValue: 'Add' })}
              onAddProduct={(p) => addRecommendedProduct(p, 'cart_fbt')}
              onOpenProduct={(id) => navigate(`/products/${id}`)}
            />

            <CartRetentionRail
              title={t('storefront.cart_similar_title', {
                defaultValue: 'Similar to your cart',
              })}
              subtitle={t('storefront.cart_similar_subtitle', {
                defaultValue: 'More from the same categories and flavor profiles.',
              })}
              products={similarProducts}
              viewAllLabel={t('storefront.cart_suggested_products_view_all', {
                defaultValue: 'Shop all',
              })}
              onViewAll={() => navigate('/products')}
              trackContext="cart_similar"
              formatPrice={formatPrice}
              addToCartLabel={t('storefront.cart_rec_add_short', { defaultValue: 'Add' })}
              onAddProduct={(p) => addRecommendedProduct(p, 'cart_similar')}
              onOpenProduct={(id) => navigate(`/products/${id}`)}
            />

            <CartBundleRail
              title={t('storefront.cart_popular_bundles_title', {
                defaultValue: 'Popular bundles',
              })}
              subtitle={t('storefront.cart_popular_bundles_subtitle', {
                defaultValue: 'Curated sets customers often add with their cart.',
              })}
              packages={suggestedPackagesVisible}
              viewAllLabel={t('storefront.cart_suggested_packages_view_all', {
                defaultValue: 'View bundles',
              })}
              onViewAll={() => navigate('/packages')}
              formatPrice={formatPrice}
              addLabel={t('storefront.add_to_cart', { defaultValue: 'Add' })}
              onAddPackage={(pkg) => addLine({ kind: 'package', packageId: pkg.id, qty: 1 })}
              onOpenPackage={(slug) => navigate(`/packages/${encodeURIComponent(slug)}`)}
            />

            <CartRetentionRail
              title={t('storefront.cart_bestsellers_title', { defaultValue: 'Bestsellers' })}
              subtitle={t('storefront.cart_bestsellers_subtitle', {
                defaultValue: 'Top-selling items across the store — not limited to one aisle.',
              })}
              products={exploreProducts}
              viewAllLabel={t('storefront.cart_suggested_products_view_all', {
                defaultValue: 'Shop all',
              })}
              onViewAll={() => navigate('/products')}
              trackContext="cart_bestsellers"
              formatPrice={formatPrice}
              addToCartLabel={t('storefront.cart_rec_add_short', { defaultValue: 'Add' })}
              onAddProduct={(p) => addRecommendedProduct(p, 'cart_bestsellers')}
              onOpenProduct={(id) => navigate(`/products/${id}`)}
            />

            <CartRetentionRail
              title={t('storefront.cart_complete_order_title', {
                defaultValue: 'Complete your order',
              })}
              subtitle={t('storefront.cart_complete_order_subtitle', {
                defaultValue: 'A few more staples to round out your delivery.',
              })}
              products={completeOrderProducts}
              viewAllLabel={t('storefront.cart_suggested_products_view_all', {
                defaultValue: 'Shop all',
              })}
              onViewAll={() => navigate('/products')}
              trackContext="cart_complete_order"
              formatPrice={formatPrice}
              addToCartLabel={t('storefront.cart_rec_add_short', { defaultValue: 'Add' })}
              onAddProduct={(p) => addRecommendedProduct(p, 'cart_complete_order')}
              onOpenProduct={(id) => navigate(`/products/${id}`)}
            />
          </div>
        ) : exploreProducts.length > 0 ? (
          <div className="mt-10">
            <CartRetentionRail
              title={t('storefront.cart_empty_suggestions_title', {
                defaultValue: 'Popular right now',
              })}
              subtitle={t('storefront.cart_empty_suggestions_subtitle', {
                defaultValue: 'Start with these customer favorites.',
              })}
              products={exploreProducts}
              viewAllLabel={t('storefront.cart_suggested_products_view_all', {
                defaultValue: 'Shop all',
              })}
              onViewAll={() => navigate('/products')}
              trackContext="cart_empty_suggestions"
              formatPrice={formatPrice}
              addToCartLabel={t('storefront.cart_rec_add_short', { defaultValue: 'Add' })}
              onAddProduct={(p) => addRecommendedProduct(p, 'cart_empty_suggestions')}
              onOpenProduct={(id) => navigate(`/products/${id}`)}
            />
          </div>
        ) : null}
      </div>
      {lines.length > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur lg:hidden">
          <div className="mx-auto w-full max-w-7xl pr-16">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium text-slate-600">{t('storefront.subtotal')}</span>
              <span className="text-lg font-bold text-slate-900">
                {formatMajorInCurrency(totalsDisplayMajor.full, totalsDisplayMajor.code)}
              </span>
            </div>
            <button
              type="button"
              onClick={goCheckout}
              disabled={
                unavailableIds.length > 0 ||
                unavailablePackageIds.length > 0 ||
                checkoutBlocked
              }
              className="w-full rounded-full bg-[#00b853] px-6 py-3 text-base font-semibold text-white hover:bg-[#009946] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t('storefront.order_now', { defaultValue: 'Order Now' })}
            </button>
          </div>
        </div>
      ) : null}
    </StorefrontLayout>
  );
}

